<template>
  <app-content>
    <page-title>Photo-Upload</page-title>
    <main id="photo-upload">
      <div class="container">
        <b-form id="upload-form">
          <b-card header="Foto-Upload" no-body>
            <b-card-body>
              <p>Du hast auf unserer Hochzeit auch Fotos gemacht? Wir würden uns freuen, wenn du sie
                mit uns teilst. Das geht ganz einfach:
              </p>
              <ol>
                <li>Wähle das Fotoalbum, dem du Bilder hinzufügen möchstes.</li>
                <li>Wähle die Fotos unten aus oder ziehe sie mit der Maus in ein freies
                  "Upload"-Feld. Falls keine Felder frei sind, kannst du weitere über "Mehr Fotos
                  hinzufügen" erzeugen.
                </li>
                <li>Klicke auf "Hochladen"</li>
                <li>Die Fotos werden nacheinander hochgeladen. Das kann etwas dauern.
                  Der Fortschritt wird unten angezeigt.
                </li>
                <li>Sie erscheinen <strong>noch nicht in der Galerie</strong>. Jedoch können wir
                  sie bereits sehen und geben sie später für die Galerie frei.
                </li>
              </ol>
            </b-card-body>
            <b-card-body>
              <b-form-group label="Album" label-cols-sm="4" label-cols-lg="2" class="album-select">
                <b-form-select v-model="album" :options="albums" value-field="_id"
                               text-field="name" :select-size="4" :disabled="loading"
                               :state="album != null" required/>
                <b-form-invalid-feedback :state="album != null">
                  Bitte wähle ein Album aus!
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group :label="'Upload ' + (idx + 1)" v-for="(photo, idx) in photos"
                            :key="idx" label-cols-sm="4" label-cols-lg="2" class="photo-input">
                <div class="d-flex">
                  <b-form-file v-model="photos[idx]" :disabled="loading" :state="Boolean(photo)"
                               multiple accept="image/*"
                               placeholder="Dateien auswählen"
                               drop-placeholder="Hier fallen lassen...">
                    <template slot="file-name" slot-scope="{ names }">
                      <b-badge variant="primary">{{ names[0] }}</b-badge>
                      <b-badge v-if="names.length > 1" variant="primary" class="ml-1">
                        + {{ names.length - 1 }} weitere Dateien
                      </b-badge>
                    </template>
                  </b-form-file>
                  <b-button variant="danger" @click="photos.splice(idx, 1)"
                            :disabled="loading">Entfernen
                  </b-button>
                </div>
              </b-form-group>
              <b-button block id="add-other" variant="outline-secondary" :disabled="loading"
                        @click="photos.push(null)">
                Mehr Fotos hinzufügen
              </b-button>
            </b-card-body>
            <b-card-body>
              <b-card-title>Fortschritt</b-card-title>
              <b-progress :max="nonNullPhotos.length" striped :animated="loading" height="2rem"
                          v-b-popover.top.manual="successPopover">
                <b-progress-bar :value="uploaded" :label="`${uploaded} / ${nonNullPhotos.length}`"/>
              </b-progress>
            </b-card-body>
            <b-card-footer id="buttons" :class="{valid: valid}">
              <div>
                <b-button id="reset" variant="danger" :disabled="loading"
                          @click="reset">Zurücksetzen
                </b-button>
                <b-button id="submit" variant="success" :disabled="loading || !valid"
                          @click="submit">{{nonNullPhotos.length}} Fotos hochladen
                </b-button>
              </div>
            </b-card-footer>
          </b-card>
        </b-form>
      </div>
    </main>
  </app-content>
</template>

<script>
import PageTitle from '@/components/PageTitle.vue';
import AppContent from '@/components/technical/AppContent.vue';

import WPlannerApi from '@/mixins/wplanner-api';

export default {
  name: 'Photo-Upload',
  components: {
    PageTitle,
    AppContent,
  },
  mixins: [WPlannerApi],

  data() {
    return {
      photos: [null],
      album: null,
      albums: [],
      uploaded: 0,

      successPopover: {
        title: 'Upload beendet',
        content: 'Die Fotos wurden hochgeladen',
        variant: 'success',
        id: 'upload-success-popover',
      },

      error: null,
    };
  },

  computed: {
    nonNullPhotos() {
      return this.photos.filter(p => p != null)
        .flatMap(p => p);
    },

    valid() {
      return this.nonNullPhotos.length > 0 && this.album != null;
    },

    errorPopover() {
      return {
        title: 'Ein Fehler ist aufgetreten',
        content: () => this.error,
        variant: 'danger',
        html: true,
        id: 'upload-error-popover',
      };
    },
  },

  methods: {
    reset() {
      this.getGalleryAlbums(this, (response) => {
        this.album = null;
        this.albums = response.data;

        this.photos = [null];
        this.uploaded = 0;
      });
    },

    submit() {
      this.uploadRequest(this, this.album, this.nonNullPhotos, (response) => {
        const uploadGrants = response.data;

        this.uploaded = 1;

        this.uploadFilesToS3(this, this.nonNullPhotos, uploadGrants, () => {
          this.uploaded += 1;
        }, () => {
          this.$root.$emit('bv::show::popover', 'upload-success-popover');
          window.setTimeout(() => this.$root.$emit('bv::hide::popover', 'upload-success-popover'),
            5000);
          this.reset();
        }, (error) => {
          if (error.response) {
            this.error = this.statusToMessage(error.response.status);
          } else {
            this.error = error.message;
          }

          this.$root.$emit('bv::show::popover', 'upload-error-popover');
          window.setTimeout(() => this.$root.$emit('bv::hide::popover', 'upload-error-popover'),
            15000);
        });
      }, (error) => {
        if (error.response) {
          this.error = this.statusToMessage(error.response.status);
        } else {
          this.error = error.message;
        }

        this.$root.$emit('bv::show::popover', 'upload-error-popover');
        window.setTimeout(() => this.$root.$emit('bv::hide::popover', 'upload-error-popover'),
          15000);
      });
    },

    statusToMessage(status) {
      if (status === 401) {
        return 'Du bist nicht berechtigt, den Upload durchzuführen. Versuche das '
          + 'folgende: <ul>'
          + '<li><b>Lade die Website neu.</b> Vielleicht ist deine Anmeldung abgelaufen.</li></ul>';
      }
      if (status === 403) {
        return 'Du bist nicht berechtigt, den Upload durchzuführen. Versuche das '
          + 'folgende: <ul>'
          + '<li><b>Lade die Website neu.</b> Vielleicht ist deine Anmeldung abgelaufen.</li>'
          + '<li><b>Starte den Upload erneut.</b>.</li></ul>';
      }
      return 'Unerwarteter Fehler.';
    },
  },

  mounted() {
    this.reset();
  },
};
</script>

<style lang="scss">
main#photo-upload {
  flex: 1 0 auto;

  padding: 1.5rem;

  #upload-form {
    text-align: initial;

    .card-body {
      border-bottom: 1px solid rgb(0, 0, 0, 0.125);
      background-color: $body-bg;

      ol {
        margin-bottom: 0;
      }

      &+.card-body {
        padding-top: 1.25rem;
      }
    }

    .custom-file-input:lang(de) ~ .custom-file-label::after {
      content: 'Durchsuchen';
    }

    .custom-file-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &>span.form-file-text {
        padding: 0;
        border: 0;
        margin: 0;
      }
    }

    .b-form-file + button {
      margin-left: 1rem;
    }
  }
}
</style>
